import { useLoaMedicalTestContext } from "../../../Contexts/LoaMedicalTestContext";
import CustomizedProgressBars from "../../../Components/LinearDeterminate";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import { removeDuplicatesByField } from "../../../utilities/helper";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CancelLOARequestModal from "../CancelLOARequestModal";
import { baseUrl } from "../../../utilities/configurations";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { ScrollBar } from "../../../Components/ScrollBar";
import { useAuth } from "../../../Contexts/AuthContext";
import { CircularProgress } from "@mui/material";
import Button from "../../../Components/Button";
import RequestForm from "./RequestForm";
import Policies from "./Policies";
import { useEffect } from "react";
import Summary from "./Summary";
import Success from "./Success";
import Upload from "./Upload";
import axios from "axios";
import ErrorModal from "../../../Components/ErrorModal";

function LoaMedicalTest() {
  const {
    setDisplayAlert,
    setCurrentStep,
    setErrorField,
    setAllDoctors,
    displayAlert,
    setLoadState,
    currentStep,
    errorField,
    loadState,
    navigate,
    formData,
  } = useLoaMedicalTestContext();

  const { physicians, token } = useAuth();

  useEffect(() => {
    const uniqueData = removeDuplicatesByField(physicians, "Physician");
    setAllDoctors(uniqueData);
  }, [physicians]);

  const calculateProgress = () => {
    let totalStep = 4;
    return (currentStep / totalStep) * 100;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      validateStepOne();
    } else if (currentStep === 2) {
      validateStepTwo();
    } else if (currentStep === 3) {
      validateStepThree();
    } else if (currentStep === 4) {
      submitRequest();
    }
  };

  const validateStepOne = () => {
    setCurrentStep(currentStep + 1);
  };

  const validateStepTwo = () => {
    if (!formData.requestingDoctorName) {
      setErrorField("requestingDoctorName");
    }else if (!formData.hospitalName) {
      setErrorField("hospitalName");
    } else if (formData.procedures.length <= 0) {
      setErrorField("procedures");
    } else if (!formData.diagnosis) {
      setErrorField("diagnosis");
    } else if (!formData.chiefComplaint) {
      setErrorField("chiefComplaint");
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const validateStepThree = () => {
    setCurrentStep(currentStep + 1);
  };

  const submitRequest = async () => {
    setLoadState("submit");
    let icdList = [
      {
        icdCode: formData.diagnosis.icdCode,
        icdDesc: formData.diagnosis.icdDescription,
      },
    ];
    let data = new FormData();
    data.append("cardNo", formData.selectedPolicy.cardNo);
    data.append("providerCode", formData.hospitalName.providerCode);
    data.append("providerName", formData.hospitalName.providerName);
    data.append(
      "physicianCode",
      formData.requestingDoctorName.Physician?.split(":")[1].trim() || ""
    );
    data.append(
      "physicianName",
      formData.requestingDoctorName.Physician?.split(":")[0].trim() || ""
    );
    data.append("chiefComplaint", formData.chiefComplaint);
    data.append("mobileNo", formData.selectedPolicy.mobileNo);
    data.append("icdList", JSON.stringify(icdList));
    data.append("procedureList", JSON.stringify(formData.procedures));
    data.append("file", formData.attachment);
    try {
      const res = await axios.post(`${baseUrl}/LOA/medical-test`, data, {
        headers: {
          Authorization: `Bearer ${token.accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      });
      if (res.status === 200) {
        if(res.data.status){
          setLoadState("submit success");
        }else{
          setLoadState("submit failed");
        }
      } else {
        setLoadState("submit failed");
      }
    } catch (error) {
      setLoadState("submit failed");
      console.log(error);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleBackToDashboard = () => {
    navigate("/home", { replace: true });
  };

  const handleCancelRequest = () => {
    navigate(-1);
    setDisplayAlert(false);
  };

  const renderClassName = () => {
    let className =
      "w-full min-h-[200px] h-[calc(100vh-165px)] sm:h-full rounded";
    if (currentStep === 1) {
      className =
        "w-full min-h-[200px] h-[calc(100vh-265px)] sm:h-full rounded";
    }
    return className;
  };

  const handleCloseError=()=>{
    setLoadState("");
  }

  let backButton = (
    <button disabled>
      <KeyboardArrowLeft className="invisible" />
    </button>
  );

  if (currentStep >= 2 && currentStep <= 4) {
    backButton = (
      <button onClick={handlePrev}>
        <KeyboardArrowLeft />
      </button>
    );
  }

  let closeButton = (
    <button onClick={() => setDisplayAlert(true)}>
      <CloseOutlinedIcon />
    </button>
  );

  let buttonTxt = "Continue";

  if (currentStep >= 4) {
    buttonTxt = "Submit Request";
  }

  let isButtonDisabled =
    loadState === "submit" ||
    (currentStep === 3 && !formData.attachment) ||
    errorField === "attachment" ||
    !formData.selectedPolicy ||
    (currentStep === 2 && !formData.conformeAccepted);

  let nextButton = (
    <Button
      disabled={isButtonDisabled}
      className={`h-12 w-full transition ease-in-out delay-75 rounded-xl
          ${
            isButtonDisabled
              ? "bg-[#F6F7F7] text-gray-400"
              : "bg-[#0947AB] text-white"
          }`}
      onClick={handleNext}
    >
      {loadState === "submit" ? <CircularProgress size={"1rem"} /> : buttonTxt}
    </Button>
  );

  let content = null;
  if (currentStep === 1) {
    content = <Policies />;
  } else if (currentStep === 2) {
    content = <RequestForm />;
  } else if (currentStep === 3) {
    content = <Upload />;
  } else if (currentStep === 4) {
    content = <Summary />;
  }

  if (loadState === "submit success") {
    closeButton = null;
    backButton = null;

    nextButton = (
      <Button
        className={`h-12 w-full transition ease-in-out delay-75 rounded-xl bg-[#0947AB] text-white`}
        onClick={handleBackToDashboard}
      >
        Go back to dashboard
      </Button>
    );
    content = <Success />;
  }

  let consent = (
    <div className="p-4 pb-0">
      <div className="text-center p-3 rounded-lg bg-[#d8e7ff] text-[#696e78] text-sm">
        As required by law, all legal-aged dependents (18 years of age and
        above) need to to create their own account and consent to Maxicare's
        Terms and Conditions to access their policies/card numbers.
      </div>
    </div>
  );

  if (currentStep > 1) {
    consent = null;
  }

  return (
    <div className="flex flex-col min-h-screen sm:items-center sm:bg-[#E7EEFA]">
      <div className="flex flex-col flex-1 sm:flex-none bg-white sm:w-148 sm:items-center rounded-xl sm:my-24">
        <div className="w-full">
          <div className="flex flex-row items-center p-3 border-b">
            {backButton}
            <div className="text-center text-md font-bold flex-1">
              Medical test LOA request
            </div>
            {closeButton}
          </div>
          <div className="p-3">
            <CustomizedProgressBars
              className={currentStep >= 4 ? "invisible" : ""}
              value={calculateProgress()}
            />
          </div>
        </div>
        <ScrollArea.Root className="w-full rounded overflow-hidden">
          <ScrollArea.Viewport
            id="scrollDiv"
            style={{ scrollBehavior: "smooth" }}
            className={renderClassName()}
          >
            {content}
          </ScrollArea.Viewport>
          <ScrollBar />
        </ScrollArea.Root>
        {consent}
        <div className="flex w-full p-4 mt-auto">{nextButton}</div>
      </div>
      <CancelLOARequestModal
        open={displayAlert}
        onClose={() => setDisplayAlert(false)}
        onCancel={handleCancelRequest}
        title={"medical test LOA"}
      />
      <ErrorModal
        open={loadState === "submit failed"}
        title={`LOA Request Failed`}
        message={`Unfortunately, we were unable to process your
          Letter of Authorization request at this time. Please try again later.`}
        onClose={handleCloseError}
      />
    </div>
  );
}

export default LoaMedicalTest;
